import { colors } from 'bench-style-constants';
import { JournalEntrySidebar } from 'journal-entry-sidebar';
import { LedgerAccountSidebar } from 'bk-web-app/ledger-account-sidebar';
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { VariableSizeList } from 'react-window';
import { Button } from 'bench-internal-theme';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';

import { NAVIGATION_WIDTHS, PERFORMANCE_CONSTANTS } from './constants';
import { useHeightCache } from './hooks/useHeightCache';
import { useGeneralLedger } from './contexts';
import BulkEditFormContainer from './BulkEditFormContainer';
import type {
  DataEntry,
  DateRange,
  GeneralLedgerFilters,
  GeneralLedgerListItem,
  LedgerAccount,
  LedgerAccountCategory,
  SidebarDescriptor,
} from './types';
import { Client } from './types';
import DismissableRightSidebar from './DismissableRightSidebar';
import { useSearchResults } from './hooks/useSearchResults';
import GeneralLedgerItemRenderer from './GeneralLedgerItemRenderer';
import LedgerNavigation from './LedgerNavigation';
import makeGeneralLedgerListItemData, { getItemKey } from './makeGeneralLedgerListItemData';
import PageControls from './PageControls';
import {
  SelectedDataEntriesConsumer,
  SelectedDataEntriesProvider,
} from './SelectedDataEntriesContext';
import sidebarTypes from './sidebarTypes';

const PageWrapper = styled.div<{ isNavigationExpanded: boolean }>`
  bottom: 0;
  display: grid;
  grid-template-areas:
    'navigation header'
    'navigation body'
    'navigation footer';
  grid-template-columns: ${({ isNavigationExpanded }) =>
      isNavigationExpanded ? NAVIGATION_WIDTHS.EXPANDED : NAVIGATION_WIDTHS.COLLAPSED}px 1fr;
  grid-template-rows: min-content 1fr;
  left: 0;
  overflow: auto;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
`;

const PageHeader = styled.div`
  background: ${colors.pearl};
  border-bottom: 1px solid ${colors.silt};
  grid-area: header;
  // Technically this should be "overflow: auto;" so that the content can be scrolled horizontally
  // when it overflows, but since the year-month picker dropdown is implemented as a nested element
  // instead of being mounted at the body it would be cut off
  overflow: visible;
  position: relative;
  z-index: 2;
`;

const PageFooter = styled.div`
  background: ${colors.pearl};
  border-top: 1px solid ${colors.silt};
  position: relative;
  z-index: 2;
`;

const HorizontalScrollContainer = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
`;

const TableContentWrapper = styled.div<{ $minWidth: number }>`
  min-width: ${props => props.$minWidth}px;
  width: 100%;
`;

const PageBody = styled.div`
  background: ${colors.white};
  grid-area: body;
  overflow: auto;
  position: relative;
  z-index: 1;
`;

const PageNavigation = styled.div`
  background: ${colors.white};
  grid-area: navigation;
  overflow: visible;
  position: relative;
  z-index: 3;
  border-right: 1px solid #e0deda;
`;

const TogglePageSidebarButtonWrapper = styled.div`
  position: absolute;
  top: 20px;
  right: -12px;
`;

// Specified in the onItemsRendered prop of https://react-window.now.sh/#/api/FixedSizeList
// Represents the indices of the list items provided to a FixedSizeList or VariableSizeList that
// have been rendered. Includes both the start and end (stop) indices of the items that are rendered
// and visible, and also the indices of the items in the "overscan" area.
type ReactWindowRenderedItemsBounds = {
  overscanStartIndex: number;
  overscanStopIndex: number;
  visibleStartIndex: number;
  visibleStopIndex: number;
};

export type GeneralLedgerProps = {
  // The sorted list of data entries with links to their ledgers and journal entries
  dataEntries: Array<DataEntry>;
  // The date range currently being displayed
  dateRange: DateRange;
  // If any filters are preset, sets the initial state.filters to these values
  defaultFilters?: GeneralLedgerFilters;
  // The optional sidebar to show when the component first mounts
  defaultSidebarDescriptor?: SidebarDescriptor;
  // Whether any quality checks were triggered while compiling the query data; used for disabling
  // the "Show: Quality Issues Summary" button in the PageControls when no quality issues are found
  hasQualityIssues?: boolean;
  // The sorted list of ledger account categories with links to their ledgers and data entries
  ledgerAccountCategories: Array<LedgerAccountCategory>;
  // Optional callback function to call when any filters are updated
  onFiltersChange?: (filters: GeneralLedgerFilters) => void;
  // Updates the query params for the sidebar so that it can be bookmarked
  updateSidebarParams: (sidebarDescriptor?: SidebarDescriptor) => void;
  // Function that updates the currently displayed date range
  updateDateRange: (dateRange: DateRange) => void;
  // Map of data entries for use in the BulkEditFormContainer
  dataEntriesMap: Map<string, DataEntry>;
  // Lightning GL feature flag
  isLightningGL: boolean;
  // Client Details for Feature Flag
  client: Client;
  moneyInTransitLedgerId?: string;
  // Pre-computed potential transaction matches to avoid expensive render-time computation
  potentialMatches?: {
    creditTransactions: Map<number, DataEntry[]>;
    debitTransactions: Map<number, DataEntry[]>;
  };
  // Task data for confirm categorization
  confirmCategoryTasks?: Array<any>;
};

const eventListenerOpts: AddEventListenerOptions & EventListenerOptions = { passive: true };

const GeneralLedger: React.FC<GeneralLedgerProps> = ({
  dataEntries,
  dateRange,
  defaultFilters,
  defaultSidebarDescriptor,
  hasQualityIssues = false,
  ledgerAccountCategories,
  onFiltersChange,
  updateSidebarParams,
  updateDateRange,
  dataEntriesMap,
  isLightningGL,
  client,
  moneyInTransitLedgerId,
  potentialMatches,
  confirmCategoryTasks,
}) => {
  const initialFilters = useMemo(() => ({ ...defaultFilters }), [defaultFilters]);
  const initialSidebarDescriptor = useMemo(
    () => defaultSidebarDescriptor,
    [defaultSidebarDescriptor],
  );

  // Check if the confirm categorization tasks feature is enabled
  const [
    {
      featureContext: { features },
    },
  ] = useFeatureFlags();
  const isConfirmCategorizationTasksEnabled =
    features[SplitFeatureFlags.CONFIRM_CATEGORIZATION_TASKS]?.value === FeatureValue.FEATURE_ON;
  const {
    filters,
    isNavigationExpanded,
    listHeight,
    selectedDataEntry,
    sidebarDescriptor,
    sortStates,
    showSourceColumn,
    updateFilters,
    updateFirstVisibleIndex,
    updateListHeight,
    updateSelectedDataEntry,
    updateSelectedLedgerAccount,
    updateSidebarDescriptor,
    clearSidebar,
    toggleNavigation,
    updateSortState,
    setItemData,
    setScrollToItem,
    filterMito,
    updateFilterMito,
  } = useGeneralLedger();

  // Calculate minimum table width based on fixed column widths
  // TODO entirely refactor this table so that it can scroll more easily and have a sticky header.
  const minTableWidth = useMemo(() => {
    let totalWidth = 0;

    // Fixed width columns (in pixels)
    totalWidth += 70; // Date
    totalWidth += 120; // Transaction Name (minimum)
    totalWidth += 40; // Icon column
    totalWidth += 150; // Contra Ledger (when visible)
    if (showSourceColumn) {
      totalWidth += 230; // Source column
    }
    totalWidth += 80; // Debit
    totalWidth += 80; // Credit
    totalWidth += 200; // Notes
    totalWidth += 32; // Speech bubble icon
    totalWidth += 32; // Categorization method icon
    totalWidth += 200; // Contractor column
    totalWidth += 200; // Sources column

    return totalWidth + 100;
  }, [showSourceColumn]);

  const listRef = useRef<VariableSizeList>(null);
  const pageBodyRef = useRef<HTMLDivElement>(null);

  const setListHeight = useCallback(() => {
    if (pageBodyRef.current) {
      const rect = pageBodyRef.current.getBoundingClientRect();
      updateListHeight(rect.height);
    }
  }, [updateListHeight]);

  const handleDataEntryToggle = useCallback(
    (dataEntry: DataEntry) => {
      const isSameAsSelectedDataEntry = selectedDataEntry && selectedDataEntry.id === dataEntry.id;
      if (isSameAsSelectedDataEntry) {
        clearSidebar();
        updateSidebarParams(undefined);
        return;
      }
      updateSelectedDataEntry(dataEntry);
      updateSelectedLedgerAccount(undefined);
      const sidebarDescriptor = {
        sidebarElementId: dataEntry.journalEntry.id,
        sidebarType: sidebarTypes.JOURNAL_ENTRY,
      };
      updateSidebarDescriptor(sidebarDescriptor);
      updateSidebarParams(sidebarDescriptor);
    },
    [
      selectedDataEntry,
      clearSidebar,
      updateSidebarParams,
      updateSelectedDataEntry,
      updateSelectedLedgerAccount,
      updateSidebarDescriptor,
    ],
  );

  const { matchingEntryIds } = useSearchResults(dataEntries, filters.search);

  const handleLedgerAccountSelect = useCallback(
    (ledgerAccount: LedgerAccount) => {
      const isSameAsSelectedLedgerAccount =
        selectedDataEntry && selectedDataEntry.id === ledgerAccount.id;
      if (isSameAsSelectedLedgerAccount) {
        clearSidebar();
        updateSidebarParams(undefined);
        return;
      }
      updateSelectedDataEntry(undefined);
      updateSelectedLedgerAccount(ledgerAccount);
      const sidebarDescriptor = {
        sidebarElementId: ledgerAccount.id,
        sidebarType: sidebarTypes.LEDGER_ACCOUNT,
      };
      updateSidebarDescriptor(sidebarDescriptor);
      updateSidebarParams(sidebarDescriptor);
    },
    [
      selectedDataEntry,
      clearSidebar,
      updateSidebarParams,
      updateSelectedDataEntry,
      updateSelectedLedgerAccount,
      updateSidebarDescriptor,
    ],
  );

  // Memoize the arguments object to reduce dependency tracking
  const listItemDataArgs = useMemo(
    () => ({
      dataEntries,
      dateRange,
      hasQualityIssues: !!hasQualityIssues,
      ledgerAccountCategories,
      moneyInTransitLedgerId,
      search: filters.search,
      selectedDataEntry,
      showAccounts: !!filters.showAccounts,
      showQualityIssues: !!filters.showQualityIssues,
      taskStatusFilter: filters.taskStatusFilter || 'none',
      showSourceColumn,
      showTasksColumn: isConfirmCategorizationTasksEnabled,
      sortStates,
      updateSortState,
      filterMito,
      updateFilterMito,
      toggleDataEntry: handleDataEntryToggle,
      toggleLedgerAccount: handleLedgerAccountSelect,
      client,
      matchingEntryIds,
      potentialMatches,
    }),
    [
      dataEntries,
      dateRange,
      hasQualityIssues,
      ledgerAccountCategories,
      moneyInTransitLedgerId,
      filters.search,
      filters.showAccounts,
      filters.showQualityIssues,
      filters.taskStatusFilter,
      selectedDataEntry,
      showSourceColumn,
      isConfirmCategorizationTasksEnabled,
      sortStates,
      updateSortState,
      filterMito,
      updateFilterMito,
      handleDataEntryToggle,
      handleLedgerAccountSelect,
      client,
      matchingEntryIds,
      potentialMatches,
    ],
  );

  const itemData: GeneralLedgerListItem[] = useMemo(() => {
    return makeGeneralLedgerListItemData(listItemDataArgs);
  }, [listItemDataArgs]);

  // Sync itemData to context - must happen in useEffect to avoid setState during render
  useEffect(() => {
    setItemData(itemData);
  }, [itemData, setItemData]);

  // Use height caching hook
  const { getItemSize, clearCache, shouldClearCache } = useHeightCache(itemData);

  // Clear height cache and reset list when data changes significantly
  useEffect(() => {
    if (shouldClearCache()) {
      clearCache();

      if (listRef.current) {
        listRef.current.resetAfterIndex(0, false);
      }
    }
  }, [shouldClearCache, clearCache]);

  const scrollToItem = useCallback(
    (listItemIndex: number) => {
      if (!listRef.current) return;

      const currentItemData = itemData || [];
      if (listItemIndex < 0 || listItemIndex >= currentItemData.length) {
        return;
      }

      listRef.current.scrollToItem(listItemIndex, 'start');
    },
    [itemData],
  );

  useEffect(() => {
    window.addEventListener('resize', setListHeight, eventListenerOpts);
    setListHeight();

    return () => {
      window.removeEventListener('resize', setListHeight, eventListenerOpts);
    };
  }, [setListHeight]);

  useEffect(() => {
    setScrollToItem(scrollToItem);
  }, [scrollToItem, setScrollToItem]);

  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      updateFilters(initialFilters);
    }
    if (initialSidebarDescriptor) {
      updateSidebarDescriptor(initialSidebarDescriptor);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const itemCount = itemData.length;

  const handleItemsRendered = useCallback(
    ({ visibleStartIndex }: ReactWindowRenderedItemsBounds) => {
      updateFirstVisibleIndex(visibleStartIndex);
    },
    [updateFirstVisibleIndex],
  );

  const handleSidebarClear = useCallback(() => {
    clearSidebar();
    updateSidebarParams(undefined);
  }, [clearSidebar, updateSidebarParams]);

  const availableDataEntries = useMemo(() => {
    if (isLightningGL) return undefined;

    const dataEntriesArray = itemData
      .map(item => item.itemProps.dataEntry)
      .filter((dataEntry): dataEntry is DataEntry => !!dataEntry)
      .filter((dataEntry, index, list) => list.indexOf(dataEntry) === index);

    return dataEntriesArray;
  }, [itemData, isLightningGL]);

  return (
    <SelectedDataEntriesProvider>
      <>
        <PageWrapper isNavigationExpanded={isNavigationExpanded}>
          <PageHeader>
            <PageControls hasQualityIssues={hasQualityIssues} />
          </PageHeader>
          <PageNavigation>
            <TogglePageSidebarButtonWrapper>
              <Button
                data-heap-id={`general-ledger-${
                  isNavigationExpanded ? 'hide' : 'show'
                }-sidebar-button`}
                icon={isNavigationExpanded ? 'doubleCaretLeft' : 'doubleCaretRight'}
                size="small"
                styleType="primary"
                onClick={toggleNavigation}
              />
            </TogglePageSidebarButtonWrapper>
            {isNavigationExpanded && <LedgerNavigation />}
          </PageNavigation>
          <PageBody ref={pageBodyRef}>
            <HorizontalScrollContainer>
              <TableContentWrapper $minWidth={minTableWidth}>
                {
                  // The VariableSizeList component requires the height of the list in order to render
                  // because it needs it to calculate which list items to render based on whether they're
                  // visible in the window
                  typeof listHeight === 'number' && (
                    <VariableSizeList
                      ref={listRef}
                      height={listHeight}
                      itemCount={itemCount}
                      itemData={itemData}
                      itemKey={getItemKey}
                      itemSize={getItemSize}
                      onItemsRendered={handleItemsRendered}
                      overscanCount={PERFORMANCE_CONSTANTS.OVERSCAN_COUNT}
                    >
                      {GeneralLedgerItemRenderer}
                    </VariableSizeList>
                  )
                }
              </TableContentWrapper>
            </HorizontalScrollContainer>
          </PageBody>
          <PageFooter>
            <SelectedDataEntriesConsumer>
              {({ selectedDataEntryIds }) =>
                !!selectedDataEntryIds.length && (
                  <BulkEditFormContainer
                    availableDataEntriesMap={dataEntriesMap}
                    availableDataEntries={availableDataEntries || []}
                    isLightningGL={isLightningGL}
                    dateRange={dateRange}
                    filters={filters}
                    client={client}
                    dismissSideBar={handleSidebarClear}
                  />
                )
              }
            </SelectedDataEntriesConsumer>
          </PageFooter>
        </PageWrapper>
        {sidebarDescriptor && sidebarDescriptor.sidebarType === sidebarTypes.JOURNAL_ENTRY && (
          <DismissableRightSidebar dismissSidebar={handleSidebarClear}>
            <JournalEntrySidebar
              dismiss={handleSidebarClear}
              journalEntryId={sidebarDescriptor.sidebarElementId}
              key={sidebarDescriptor.sidebarElementId}
            />
          </DismissableRightSidebar>
        )}
        {sidebarDescriptor && sidebarDescriptor.sidebarType === sidebarTypes.LEDGER_ACCOUNT && (
          <DismissableRightSidebar dismissSidebar={handleSidebarClear}>
            <LedgerAccountSidebar
              dismiss={handleSidebarClear}
              ledgerAccountId={sidebarDescriptor.sidebarElementId}
            />
          </DismissableRightSidebar>
        )}
      </>
    </SelectedDataEntriesProvider>
  );
};

export default GeneralLedger;
